import React, { ReactNode, useEffect, useRef } from "react";
import Col from "react-bootstrap/Col";
import Image from "react-bootstrap/Image";
import Container from "react-bootstrap/Container";
import { useNavigate } from "react-router-dom";

import "./DefaultOnboardingStatusPage.scss";
import { handleGetOnboardingState } from "../../../api-clients/userAccountClient";
import { OnboardingState } from "@nestcoinco/hyper-api-gateway-api-client";
import { OnboardingSteps } from "../../../utils/constants";
import FixedFooter from "../../../components/FixedFooter/FixedFooter";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import { useAtomValue } from "jotai";
import { userAtom } from "../../../jotai/atoms/user";

type IDefaultOnboardingStatusPageProps = {
  titleText: ReactNode;
  secondaryText: string;
  primaryImage: string;
  secondaryIcon?: ReactNode;
  redirectDelay?: number;
  redirectPath?: string;
  metaData?: {
    title?: string;
    description?: string;
    image?: string;
  };
  stayOnPage?: boolean;
  nextStep?: OnboardingSteps;
};

function DefaultOnboardingStatusPage({
  titleText,
  secondaryText,
  primaryImage,
  secondaryIcon,
  redirectPath,
  stayOnPage,
  nextStep,
  redirectDelay = 2000,
}: IDefaultOnboardingStatusPageProps) {
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const user = useAtomValue(userAtom);
  const accessToken = user?.tokens?.access;
  const { goToStep } = useOnboardingSteps();

  useEffect(() => {
    if (!accessToken || stayOnPage) return;
    const navigateAfterDelay = (step: OnboardingSteps) => {
      const timeoutId = setTimeout(() => {
        goToStep(step);
      }, redirectDelay);

      timeoutRef.current = timeoutId;
    };

    (async () => {
      if (nextStep) {
        navigateAfterDelay(nextStep);
        return;
      }

      const onboardingState = await handleGetOnboardingState();

      if (!onboardingState) {
        navigateAfterDelay(OnboardingSteps.FIND_FRIENDS);
        return;
      }

      const onboardingStep = getOnboardingStep(onboardingState);
      navigateAfterDelay(onboardingStep);

      return () => clearTimeout(timeoutRef.current);
    })();
  }, [
    accessToken,
    goToStep,
    navigate,
    nextStep,
    redirectDelay,
    redirectPath,
    stayOnPage,
  ]);

  return (
    <>
      <Container
        fluid
        className="MobileOnly position-relative defaultOnboardingStatusPage"
      >
        <Col className="d-flex justify-content-center primaryImageContainer">
          <Image src={primaryImage} className="primaryImage" />
        </Col>
        <Col className="d-flex flex-column ">
          <Col className="titleTextContainer d-flex justify-content-center">
            <h1 className="text-white mb-3">{titleText}</h1>
          </Col>
          <p className="secondary-text d-flex justify-content-center text-center px-5 text-white">
            {secondaryText}
          </p>

          <FixedFooter
            className="d-flex justify-content-center secondaryImageContainer"
            style={{ paddingBottom: 66 }}
          >
            {secondaryIcon}
          </FixedFooter>
        </Col>
      </Container>
    </>
  );
}

export default DefaultOnboardingStatusPage;

function getOnboardingStep({
  claimedUsername,
  usedReferralCode,
}: OnboardingState): OnboardingSteps {
  switch (true) {
    case claimedUsername:
      return OnboardingSteps.FIND_FRIENDS;
    case usedReferralCode:
      return OnboardingSteps.CLAIM_USERNAME;
    default:
      return OnboardingSteps.USE_REFERRAL_CODE;
  }
}
